import type { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import type { ViewerAdapter } from '../viewer/adapter.js';

export interface CameraTarget {
  pos: readonly [number, number, number];
  lookAt: readonly [number, number, number];
  fov: number;
}

export interface FlyToOptions {
  durationMs: number;
  signal?: AbortSignal;
  onTick?: (progress: number) => void;
}

const FOV_MIN = 10;
const FOV_MAX = 170;

// Symmetric ease-in-out cubic. f(0)=0, f(0.5)=0.5, f(1)=1. Monotonic on [0,1].
export function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

function lerp3(
  a: readonly [number, number, number],
  b: readonly [number, number, number],
  t: number,
): [number, number, number] {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

export async function flyToCamera(
  adapter: ViewerAdapter,
  controls: OrbitControls | null,
  from: CameraTarget,
  to: CameraTarget,
  opts: FlyToOptions,
): Promise<void> {
  const clampedToFov = Math.max(FOV_MIN, Math.min(FOV_MAX, to.fov));
  const clampedFromFov = Math.max(FOV_MIN, Math.min(FOV_MAX, from.fov));

  if (opts.durationMs <= 0) {
    adapter.setCamera(to.pos, to.lookAt, clampedToFov);
    if (controls !== null) {
      controls.target.set(to.lookAt[0], to.lookAt[1], to.lookAt[2]);
      controls.update();
    }
    opts.onTick?.(1);
    return;
  }

  const start = performance.now();
  return new Promise<void>((resolve, reject) => {
    let raf = 0;
    const cleanup = (): void => {
      if (opts.signal !== undefined) {
        opts.signal.removeEventListener('abort', onAbort);
      }
    };
    const onAbort = (): void => {
      cancelAnimationFrame(raf);
      cleanup();
      reject(new DOMException('aborted', 'AbortError'));
    };
    if (opts.signal !== undefined) {
      if (opts.signal.aborted) {
        reject(new DOMException('aborted', 'AbortError'));
        return;
      }
      opts.signal.addEventListener('abort', onAbort, { once: true });
    }
    const tick = (): void => {
      const elapsed = performance.now() - start;
      const t = Math.min(1, elapsed / opts.durationMs);
      const e = easeInOutCubic(t);
      const pos = lerp3(from.pos, to.pos, e);
      const look = lerp3(from.lookAt, to.lookAt, e);
      const fov = clampedFromFov + (clampedToFov - clampedFromFov) * e;
      adapter.setCamera(pos, look, fov);
      if (controls !== null) {
        controls.target.set(look[0], look[1], look[2]);
        controls.update();
      }
      opts.onTick?.(t);
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        cleanup();
        resolve();
      }
    };
    raf = requestAnimationFrame(tick);
  });
}
